import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Target,
  Map,
  BookOpen,
  BarChart3,
  FileText,
  Users,
} from "lucide-react";

/* ================= SECTION ================= */

const Section = styled.section`
  position: relative;
  padding: 200px 20px;
  overflow: hidden;

  background: radial-gradient(
      900px at 16% 0%,
      rgba(245, 158, 11, 0.12),
      transparent 55%
    ),
    radial-gradient(700px at 84% 30%, rgba(56, 189, 248, 0.14), transparent 60%),
    ${({ theme }) => theme.colors.bg};
`;

/* ================= CONTAINER ================= */

const Container = styled.div`
  max-width: 1200px;
  margin: auto;
  position: relative;
  z-index: 1;
`;

/* ================= HEADER ================= */

const Heading = styled(motion.h2)`
  text-align: center;
  font-size: clamp(42px, 6vw, 72px);
  font-weight: 1000;
  line-height: 1.05;
  letter-spacing: -1px;
  color: ${({ theme }) => theme.colors.text};
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #f59e0b, #38bdf8, #22c55e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Sub = styled(motion.p)`
  text-align: center;
  max-width: 780px;
  margin: 32px auto 0;
  font-size: 20px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= GRID ================= */

const Grid = styled(motion.div)`
  margin-top: 120px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

/* ================= CARD ================= */

const Card = styled(motion.div)`
  position: relative;
  padding: 56px 44px 48px;
  border-radius: 28px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.02),
    rgba(255, 255, 255, 0.01)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  overflow: hidden;

  box-shadow: 0 26px 70px rgba(2, 6, 23, 0.7);
`;

/* ================= STEP NUMBER ================= */

const StepNumber = styled.div`
  position: absolute;
  top: -26px;
  left: 36px;
  font-size: 72px;
  font-weight: 1000;
  opacity: 0.07;
  color: ${({ theme }) => theme.colors.text};
  pointer-events: none;
`;

/* ================= ICON ================= */

const IconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  margin-bottom: 28px;
  color: #fff;

  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  box-shadow: 0 0 40px rgba(56, 189, 248, 0.35);
`;

/* ================= TEXT ================= */

const Title = styled.h4`
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.text};
`;

const Text = styled.p`
  font-size: 17px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= ANIMATIONS ================= */

const container = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.18,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

/* ================= DATA ================= */

const FEATURES = [
  {
    icon: Target,
    title: "Start with your goal, not random content",
    text: "The roadmap adapts to level, direction, and ambition so users stop learning blindly.",
  },
  {
    icon: Map,
    title: "Phase -> Week -> Day structure",
    text: "Every step is sequenced so users always know what to do next without overthinking.",
  },
  {
    icon: BookOpen,
    title: "Daily tasks and project-based learning",
    text: "The product pushes users to build, practice, and ship instead of just consuming tutorials.",
  },
  {
    icon: BarChart3,
    title: "Progress, streaks, and visible momentum",
    text: "Users see proof of consistency instead of relying on motivation alone.",
  },
  {
    icon: FileText,
    title: "Resume support built on real work",
    text: "Projects, execution, and proof of work create stronger career signals than passive certificates.",
  },
  {
    icon: Users,
    title: "Mentor support when consistency gets hard",
    text: "Users can get guidance, unblock doubts, and stay accountable while following the roadmap.",
  },
];

/* ================= COMPONENT ================= */

export function SolutionSection() {
  return (
    <Section id="solution-section">
      <Container>
        <Heading
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          Not another course.
          <br />
          <Highlight>
            A career system with daily execution and visible progress.
          </Highlight>
        </Heading>

        <Sub
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          Know what to learn, what to build, and what to ignore.
          <br />
          `skilll.dev` gives users structure so effort compounds instead of
          scattering.
        </Sub>

        <Grid
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-150px" }}
        >
          {FEATURES.map((item, i) => {
            const Icon = item.icon;
            return (
              <Card
                key={i}
                variants={fadeUp}
                whileHover={{ y: -16 }}
                transition={{ type: "spring", stiffness: 140 }}
              >
                <StepNumber>{i + 1}</StepNumber>

                <IconWrap>
                  <Icon size={30} />
                </IconWrap>

                <Title>{item.title}</Title>
                <Text>{item.text}</Text>
              </Card>
            );
          })}
        </Grid>
      </Container>
    </Section>
  );
}
